/**
 * views/declViews.ts —— 视图声明的**唯一入口**（★F-01／F-10：第二真值源消灭）
 *
 * 真源＝后端 `src/sononux/decl/views.yaml`（`views.<name>`）；前端**不再持**在盘 `views/*.yaml`。
 * 本件只做两件事：
 *   ① 构建期把真源原文经 `?raw` 读入（vite／vitest 同支持；零网络、零运行期取数）；
 *   ② 拆出 `views` 节 ⇒ `{name: 原文子文档}`（各件仍由 `loadViews.ts::validateViewDecl` 逐件校验）。
 *
 * 拆分方式：按顶层缩进切块（真源 `views:` 下的每件以两空格缩进名开头），逐块 `js-yaml` 解析，
 *   **不重排、不改写**任何键（块内文本原样交给校验与消费方）；键名与块内 `name` 不符 ⇒ 抛
 *   `ViewDeclarationError`（防真源里写错名、被消费成幽灵视图）。
 */
import yaml from "js-yaml";
import declRaw from "../../../src/sononux/decl/views.yaml?raw";
import { E_CODES } from "../transport/errorText";

/** 视图声明错误（**定义位在本件**——loadViews 侧再导出保持既有导入面；避免循环 import） */
export class ViewDeclarationError extends Error {
  constructor(message: string) {
    super(E_CODES.VIEW_DECL + "：" + message);
    this.name = "ViewDeclarationError";
  }
}

/** 真源里 `views:` 节的起始行号（0 基）；缺节 ⇒ 抛（真源损坏不静默） */
function sectionStart(lines: readonly string[]): number {
  const idx = lines.findIndex((l) => /^views:\s*$/.test(l));
  if (idx < 0) throw new ViewDeclarationError("decl/views.yaml 缺顶层 `views:` 节");
  return idx;
}

/** 按两空格缩进键切块 ⇒ [{name, block}]（块文本＝原行，含换行） */
function splitBlocks(lines: readonly string[], from: number): Array<{ name: string; block: string }> {
  const out: Array<{ name: string; block: string }> = [];
  let cur: { name: string; lines: string[] } | null = null;
  for (let i = from + 1; i < lines.length; i += 1) {
    const line = lines[i];
    const m = /^ {2}([A-Za-z_][\w-]*):\s*$/.exec(line);
    if (m) {
      if (cur) out.push({ name: cur.name, block: cur.lines.join("\n") });
      cur = { name: m[1], lines: [] };
      continue;
    }
    if (!cur) {
      if (line.trim() === "" || /^\s*#/.test(line)) continue;   // 节头后注释/空行
      throw new ViewDeclarationError("decl/views.yaml 节内出现非切块行：" + JSON.stringify(line));
    }
    cur.lines.push(line);
  }
  if (cur) out.push({ name: cur.name, block: cur.lines.join("\n") });
  return out;
}

/** 一视图片段解析结果（键＝视图名，值＝该件声明原文） */
export interface DeclViewDoc {
  name: string;
  /** 该件的原文（去掉外层两空格缩进后原样） */
  raw: string;
  /** 解析后的声明对象（键面校验由消费方 `validateViewDecl` 做） */
  decl: Record<string, unknown>;
}

/** 全族：按真源声明序（确定性；消费方排序自负） */
export function loadDeclViews(source: string = declRaw): DeclViewDoc[] {
  const lines = source.split("\n");
  const blocks = splitBlocks(lines, sectionStart(lines));
  if (blocks.length === 0) throw new ViewDeclarationError("decl/views.yaml 的 views 节为空");
  const seen = new Set<string>();
  return blocks.map(({ name, block }) => {
    // 去两空格缩进 ⇒ 子文档（保留原行文本与注释）
    const raw = block.split("\n").map((l) => (l.startsWith("  ") ? l.slice(2) : l)).join("\n");
    const decl = yaml.load(raw) as Record<string, unknown> | null;
    if (!decl || typeof decl !== "object") {
      throw new ViewDeclarationError("视图声明块非映射：" + name);
    }
    if (decl.name !== name) {
      throw new ViewDeclarationError(
        "视图键名与声明 name 不符：" + name + " ≠ " + String(decl.name),
      );
    }
    if (seen.has(name)) throw new ViewDeclarationError("视图名重复：" + name);
    seen.add(name);
    return { name, raw, decl };
  });
}

/** 真源原文（诊断/断言面用：一行一视图片段，便于比对） */
export const DECL_VIEWS_RAW: string = declRaw;
